import { Permissions, User } from '../../types/user-types'

export function isValidUser(user: User | null) {
  if (!user) return false

  if (!user.firstName || !user.lastName || !user.permissions || !user.email) return false

  return true
}

export function can(user: User | null, permissions: string | Array<string>) {
  // Check any error conditions
  if (typeof permissions !== 'string' && !Array.isArray(permissions)) {
    throw new Error('Permission needs to be a string or array')
  }
  if (!user) return false
  const arrPermissions = Array.isArray(permissions) ? permissions : [permissions]
  const matchingPerms = user.permissions?.some((perm) => arrPermissions.includes(perm))

  return !!user.permissions && matchingPerms
}

export function canEditWell(user: User | null) {
  if (!user) return false
  // Check user permissions and user permissions for the current well
  return can(user, [Permissions.EDIT_WELL, Permissions.EDIT_WELL_UNSCOPED]) && !!user.objectPermissions?.canEdit
}

export function canViewWell(user: User | null) {
  if (!user) return false
  // Check user permissions and user permissions for the current well
  return can(user, [Permissions.VIEW_WELL]) && !!user.objectPermissions?.canView
}

export function canCreateWell(user: User | null) {
  if (!user) return false
  // Check user permissions and user permissions for the current well
  return can(user, [Permissions.CREATE_WELL, Permissions.CREATE_WELL_UNSCOPED]) && !!user.objectPermissions?.canCreate
}

export function isCouncilUserOrAdmin(user: User | null) {
  if (!user || !isValidUser(user)) return false
  // TODO: Revisit this logic at later stage
  // NOTE: 'maintain_all_users' permisison is being used as a proxy for the `system_admin` role
  return !!user.council || user.permissions?.includes(Permissions.MAINTAIN_ALL_USERS)
}

export function isDriller(user: User | null) {
  if (!user || !isValidUser(user)) return false
  if (user.council) return false

  return !!user.companyId
}

/**
 * Checks if a given user is a council admin or sys admin
 * @param user Current user
 * @returns
 */
export function isAdminUser(user: User | null) {
  return isCouncilAdmin(user) || isSystemAdmin(user)
}

/**
 * Checks if a given user is a council admin
 * @param user Current user
 * @returns
 */
export function isCouncilAdmin(user: User | null) {
  // Note:
  // We are using 'global.maintain_org_users' permission as a proxy for council admin user
  const adminPermissions: Array<string> = [Permissions.MAINTAIN_ORG_USERS]
  return !!user?.permissions?.some((perm) => adminPermissions.includes(perm))
}

/**
 * Checks if a given user is a sys admin
 * @param user Current user
 * @returns
 */
export function isSystemAdmin(user: User | null) {
  // Note:
  // We are using 'global.maintain_all_users' permission as a proxy for sys admin user
  const adminPermissions: Array<string> = [Permissions.MAINTAIN_ALL_USERS]
  return !!user?.permissions?.some((perm) => adminPermissions.includes(perm))
}

/**
 * Checks if a given user is a sys admin
 * @param user Current user
 * @returns
 */
export function isCouncilUser(user: User | null) {
  return isValidUser(user) && !!user?.council
}
